import { Controller } from 'stimulus';

export default class extends Controller {
    static get values() {
        return { state: String, expanded: Boolean };
    }

    static get targets() {
        return [
            'fields',
            'addButton',
            'countryInput',
            'stateInput',
            'countryButton',
            'stateButton',
            'dropdown',
            'stateDropdown',
            'countryName',
            'stateName',
            'stateOptions',
            'stateLabel',
        ];
    }

    connect() {
        // If secondary location already has data, show fields
        if (this.countryInputTarget.value || this.stateInputTarget.value) {
            this.showFields();
        } else {
            // Ensure required attributes are removed if fields are hidden on load
            this._toggleRequired(false);
        }

        this.fillStatesField();
        this.handleOutsideClick = this.handleOutsideClick.bind(this);
        document.addEventListener('click', this.handleOutsideClick);

        // Listen for form submission to clear fields if hidden
        this.form = this.element.closest('form');
        if (this.form) {
            this.handleFormSubmit = this.handleFormSubmit.bind(this);
            this.form.addEventListener('submit', this.handleFormSubmit);
        }
    }

    disconnect() {
        document.removeEventListener('click', this.handleOutsideClick);
        if (this.form) {
            this.form.removeEventListener('submit', this.handleFormSubmit);
        }
    }

    handleFormSubmit() {
        // If fields are hidden when submitting, clear values
        // so the database knows to delete/empty the secondary location.
        if (!this.expandedValue) {
            this._clearFieldValues();
        }
    }

    showFields() {
        this.fieldsTarget.classList.remove('hidden');
        this.addButtonTarget.classList.add('hidden');
        this.expandedValue = true;

        // Add required attributes to fields
        this._toggleRequired(true);
    }

    hideFields() {
        this.fieldsTarget.classList.add('hidden');
        this.addButtonTarget.classList.remove('hidden');
        this.expandedValue = false;

        // Remove required attributes so hidden fields don't block form
        this._toggleRequired(false);

        // Clear validation errors on hide
        this.element.querySelectorAll('.is-invalid').forEach((el) => {
            el.classList.remove('is-invalid');
            el.style.borderColor = '';
        });
        this.element
            .querySelectorAll('.client-validation-error, .sl-country-state-error')
            .forEach((el) => {
                el.remove();
            });
    }

    _clearFieldValues() {
        this.countryInputTarget.value = '';
        this.countryNameTarget.textContent = 'Select Country';
        this.stateInputTarget.value = '';
        this.stateNameTarget.textContent = 'Select State';
        if (this.hasStateOptionsTarget) {
            this.stateOptionsTarget.innerHTML = '';
        }

        this.element
            .querySelectorAll('input[type="text"]')
            .forEach((input) => {
                input.value = '';
            });
    }

    _toggleRequired(add) {
        // Country and State hidden inputs
        [this.countryInputTarget, this.stateInputTarget].forEach((input) => {
            if (add) {
                input.setAttribute('required', 'required');
            } else {
                input.removeAttribute('required');
            }
        });

        // Text inputs in the fields container (skip street_address_line2)
        this.fieldsTarget
            .querySelectorAll('input[type="text"]')
            .forEach((input) => {
                const name = input.name || '';
                if (name.includes('street_address_line2')) return;
                if (add) {
                    input.setAttribute('required', 'required');
                } else {
                    input.removeAttribute('required');
                }
            });
    }

    setCountry(event) {
        event.preventDefault();

        const country = event.target.dataset.country;
        const countryName = event.target.dataset.countryName;

        this.countryInputTarget.value = country;
        this.countryInputTarget.dispatchEvent(
            new Event('input', { bubbles: true })
        );
        this.countryNameTarget.textContent = countryName;

        // Clear validation errors
        this._clearValidationErrors();

        // Clear state when country changes
        this.stateInputTarget.value = '';

        let stateLabel2 = 'State';
        if (country === 'GB') {
            stateLabel2 = 'County';
        } else if (country === 'CA') {
            stateLabel2 = 'Province';
        }
        this.stateNameTarget.textContent = 'Select ' + stateLabel2;
        this._countryChanged = true;

        this.fillStatesField();
        this.dropdownTarget.classList.add('hidden');
    }

    setState(event) {
        event.preventDefault();

        const state = event.target.dataset.state;
        const stateName = event.target.dataset.stateName;

        this.stateInputTarget.value = state;
        this.stateInputTarget.dispatchEvent(
            new Event('input', { bubbles: true })
        );
        this.stateNameTarget.textContent = stateName;
        this.stateDropdownTarget.classList.add('hidden');
    }

    toggleDropdown(event) {
        event.stopPropagation();
        const button =
            event.target.closest(
                '[data-secondary-location-target="stateButton"]'
            ) || event.target.closest('button');

        if (button) {
            const isCountryDropdown =
                button.dataset.secondaryLocationTarget === 'countryButton';
            const isStateDropdown =
                button.dataset.secondaryLocationTarget === 'stateButton';

            if (isCountryDropdown) {
                this.dropdownTarget.classList.toggle('hidden');
                this.stateDropdownTarget.classList.add('hidden');
            } else if (isStateDropdown) {
                if (!this.countryInputTarget.value) {
                    const stateFieldWrapper =
                        this.stateButtonTarget.closest('.rounded-xl') ||
                        this.stateButtonTarget.closest('.relative');
                    if (stateFieldWrapper) {
                        stateFieldWrapper.style.borderColor = '#dc2626';
                        stateFieldWrapper.classList.add('is-invalid');
                        if (
                            !stateFieldWrapper.querySelector(
                                '.sl-country-state-error'
                            )
                        ) {
                            const errorDiv = document.createElement('div');
                            errorDiv.className =
                                'sl-country-state-error px-2 mt-1';
                            errorDiv.innerHTML =
                                '<span class="text-xs text-red-600">Please select a Country first</span>';
                            stateFieldWrapper.appendChild(errorDiv);
                        }
                    }
                    return;
                }
                this.stateDropdownTarget.classList.toggle('hidden');
                this.dropdownTarget.classList.add('hidden');
            }
        }
    }

    closeDropdown() {
        this.dropdownTarget.classList.add('hidden');
        this.stateDropdownTarget.classList.add('hidden');
    }

    handleOutsideClick(event) {
        if (!this.element.contains(event.target)) {
            this.dropdownTarget.classList.add('hidden');
            this.stateDropdownTarget.classList.add('hidden');
        }
    }

    stopPropagation(event) {
        event.stopPropagation();
    }

    async fillStatesField() {
        let stateLabel2 = 'State';
        if (this.hasStateLabelTarget) {
            if (this.countryInputTarget.value === 'GB') {
                this.stateLabelTarget.textContent = 'County*';
                stateLabel2 = 'County';
            } else if (this.countryInputTarget.value === 'CA') {
                this.stateLabelTarget.textContent = 'Province*';
                stateLabel2 = 'Province';
            } else {
                this.stateLabelTarget.textContent = 'State*';
                stateLabel2 = 'State';
            }
        }

        if (!this.countryInputTarget.value) {
            if (this.hasStateOptionsTarget) {
                this.stateOptionsTarget.innerHTML = '';
            }
            return;
        }

        if (this.hasStateOptionsTarget) {
            this.stateOptionsTarget.innerHTML = '';
        }

        try {
            const response = await fetch(
                `/states?country=${this.countryInputTarget.value}`,
                { credentials: 'same-origin' }
            );
            const data = await response.json();

            if (
                !this._countryChanged &&
                this.stateValue &&
                data[this.stateValue]
            ) {
                this.stateNameTarget.textContent = data[this.stateValue];
                this.stateInputTarget.value = this.stateValue;
            } else {
                this.stateNameTarget.textContent =
                    'Select ' + (stateLabel2 || 'State');
                this.stateInputTarget.value = '';
                this._countryChanged = false;
            }

            if (this.hasStateOptionsTarget) {
                Object.keys(data).forEach((key) => {
                    const link = document.createElement('a');
                    link.href = '#';
                    link.classList.add(
                        'text-gray-700',
                        'block',
                        'px-4',
                        'py-2',
                        'text-sm',
                        'hover:bg-gray-100'
                    );
                    link.dataset.action =
                        'click->secondary-location#setState';
                    link.dataset.state = key;
                    link.dataset.stateName = data[key];
                    link.textContent = data[key];
                    this.stateOptionsTarget.appendChild(link);
                });
            }
        } catch (error) {
            console.error('Error:', error);
        }
    }

    _clearValidationErrors() {
        const wrappers = [
            this.countryInputTarget.closest(
                '[data-controller="input-validation"]'
            ),
            this.stateInputTarget.closest('.rounded-xl'),
        ];

        wrappers.forEach((wrapper) => {
            if (!wrapper) return;
            wrapper.classList.remove('is-invalid');
            wrapper.style.borderColor = '';
            const errorMsg = wrapper.querySelector(
                '.client-validation-error'
            );
            if (errorMsg) errorMsg.remove();
        });

        const stateFieldWrapper =
            this.stateButtonTarget.closest('.rounded-xl') ||
            this.stateButtonTarget.closest('.relative');
        if (stateFieldWrapper) {
            stateFieldWrapper.classList.remove('is-invalid');
            stateFieldWrapper.style.borderColor = '';
            const stateError = stateFieldWrapper.querySelector(
                '.sl-country-state-error'
            );
            if (stateError) stateError.remove();
        }
    }
}
